import Vue from 'vue'
import VueRouter from 'vue-router'
import { spaMetaUpdateAndTrack } from 'app/utils/head'

// code splitting
/* ANALYTICS-TRACKERS ROUTES */
// NOTE: apexcharts and vue-apexcharts are part of the vendor chunk for analytics
const DashboardStatsPage = () =>
  import(
    /* webpackChunkName: "analytics-trackers" */ './components/analytics/dashboard-stats-page.vue'
  )
const DashboardWorkoutsPage = () =>
  import(
    /* webpackChunkName: "analytics-trackers" */ './components/analytics/dashboard-workouts-page.vue'
  )
const DashboardCommunityPage = () =>
  import(
    /* webpackChunkName: "analytics-trackers" */ './components/analytics/dashboard-community-page.vue'
  )
const WorkoutDetailDashboardPage = () =>
  import(
    /* webpackChunkName: "analytics-trackers" */ './components/analytics/workout-detail/dashboard-page.vue'
  )
const WorkoutDetailCompletesPage = () =>
  import(
    /* webpackChunkName: "analytics-trackers" */ './components/analytics/workout-detail/dashboard-completes-page.vue'
  )
const WorkoutDetailNotesPage = () =>
  import(
    /* webpackChunkName: "analytics-trackers" */ './components/notes/workout-detail-notes-page.vue'
  )
const OrganizationDashboardPage = () =>
  import(
    /* webpackChunkName: "analytics-trackers" */ './components/organizations/dashboard-page.vue'
  )
const OrganizationUsersPage = () =>
  import(
    /* webpackChunkName: "analytics-trackers" */ './components/organizations/dashboard-users-page.vue'
  )

/* ACHIEVEMENTS ROUTES */
const AchievementsPage = () =>
  import(/* webpackChunkName: "user" */ './components/achievements/achievements-page.vue')

/* NOTIFICATION ROUTES */
const NotificationsPage = () =>
  import(/* webpackChunkName: "user" */ './components/notifications/notifications-page.vue')

/* SEARCH ROUTES */
const VideosPage = () =>
  import(/* webpackChunkName: "search" */ './components/videos/videos-page.vue')
const SimilarVideosPage = () =>
  import(/* webpackChunkName: "search" */ './components/videos/similar-videos-page.vue')
const PlansPage = () => import(/* webpackChunkName: "search" */ './components/plans/plans-page.vue')
const ArticlesPage = () =>
  import(/* webpackChunkName: "search" */ './components/articles/articles-page.vue')

/* ROUTINES ROUTES */
// using search chunk as routine building happens to use video / favorites search
const RoutinesPage = () =>
  import(/* webpackChunkName: "search" */ './components/routines/routines-page.vue')

/* CUSTOM WORKOUTS ROUTES */
const CustomWorkoutsPage = () =>
  import(/* webpackChunkName: "search" */ './components/custom-workouts/custom-workouts-page.vue')

/* VUEX MODULES */
const LoadNotesState = (store) => {
  return import(/* webpackChunkName: "analytics-trackers" */ './store/notes/state').then(
    (notes) => {
      if (!store.state.notes) {
        store.registerModule('notes', notes.default)
      }
    },
  )
}
const LoadSearchState = (store) => {
  return import(/* webpackChunkName: "search" */ './store/search/state').then((search) => {
    search.registerSearchModule(store)
  })
}
const LoadOrganizationState = (store) => {
  return import(/* webpackChunkName: "analytics-trackers" */ './store/organizations/state').then(
    (state) => {
      state.registerOrganizationModule(store)
    },
  )
}

// attach router to vue instance
Vue.use(VueRouter)

const createSpaRoutes = ({ store }) => {
  const router = new VueRouter({
    mode: 'history',
    routes: [
      {
        name: 'Videos',
        meta: {
          title: 'Free Workout Videos',
          gaTrack: true,
          focusTitle: false, // managed on videos-page.vue
          scrollTo: null,
        },
        path: '/videos',
        component: VideosPage,
        beforeEnter: (to, from, next) => {
          // register vuex search module
          LoadSearchState(store).then(() => {
            next()
          })
        },
      },
      {
        name: 'SimilarVideos',
        meta: {
          title: 'Similar Workout Videos',
          gaTrack: true,
          focusTitle: false, // managed on similar-videos-page.vue
          scrollTo: null,
        },
        path: '/videos/similar',
        component: SimilarVideosPage,
        beforeEnter: (to, from, next) => {
          // register vuex search module
          LoadSearchState(store).then(() => {
            next()
          })
        },
      },
      {
        name: 'FavoriteVideos',
        meta: {
          title: 'Favorite Videos',
          gaTrack: true,
          focusTitle: false, // managed on videos-page.vue
          scrollTo: null,
        },
        path: '/my/favorite/videos',
        component: VideosPage,
        beforeEnter: (to, from, next) => {
          // register vuex search module
          LoadSearchState(store).then(() => {
            next()
          })
        },
      },
      {
        name: 'Plans',
        meta: {
          title: 'Workout Programs',
          gaTrack: true,
          focusTitle: false, // managed on plans-page.vue
          scrollTo: null,
        },
        path: '/plans',
        component: PlansPage,
        beforeEnter: (to, from, next) => {
          // register vuex search module
          LoadSearchState(store).then(() => {
            next()
          })
        },
      },
      {
        name: 'MyPrograms',
        meta: {
          title: 'My Programs',
          gaTrack: true,
          focusTitle: false, // managed on plans-page.vue
          scrollTo: null,
        },
        path: '/my/programs',
        component: PlansPage,
        beforeEnter: (to, from, next) => {
          // register vuex search module
          LoadSearchState(store).then(() => {
            next()
          })
        },
      },

      /* achievements */
      {
        name: 'Achievements',
        meta: {
          title: 'Achievements',
          gaTrack: true,
        },
        path: '/my/achievements/:type',
        component: AchievementsPage,
      },

      /* notifications */
      {
        name: 'Notifications',
        meta: {
          title: 'Notifications',
          gaTrack: true,
        },
        path: '/my/notifications',
        component: NotificationsPage,
      },

      /* article-search (only recipes currently, see ArticlesController->categoryIndex) */
      {
        name: 'Recipes',
        meta: {
          title: 'Recipes',
          gaTrack: true,
          focusTitle: false, // managed on articles-page.vue
          scrollTo: null,
        },
        path: '/healthy-living/healthy-recipes',
        component: ArticlesPage,
        beforeEnter: (to, from, next) => {
          // register vuex search module
          LoadSearchState(store).then(() => {
            next()
          })
        },
      },
      {
        name: 'FavoriteRecipes',
        meta: {
          title: 'Favorite Recipes',
          gaTrack: true,
          focusTitle: false, // managed on articles-page.vue
          scrollTo: null,
        },
        path: '/my/favorite/recipes',
        component: ArticlesPage,
        beforeEnter: (to, from, next) => {
          // register vuex search module
          LoadSearchState(store).then(() => {
            next()
          })
        },
      },

      /* custom-workouts */
      {
        name: 'CustomWorkouts',
        meta: {
          title: 'My Custom Workouts',
          gaTrack: true,
        },
        path: '/my/custom-workouts',
        component: CustomWorkoutsPage,
        beforeEnter: (to, from, next) => {
          // register vuex search module
          LoadSearchState(store).then(() => {
            next()
          })
        },
      },

      /* routines */
      {
        name: 'Routines',
        meta: {
          title: 'My Routines',
          gaTrack: true,
          focusTitle: false, // managed on routines-page.vue
          scrollTo: null,
        },
        path: '/my/routines',
        component: RoutinesPage,
        beforeEnter: (to, from, next) => {
          // register vuex search module
          LoadSearchState(store).then(() => {
            next()
          })
        },
      },

      /* analytics SPA routes, title should mirror what is in MyDashboardController->analyticsSPA */
      {
        name: 'DashboardStats',
        meta: {
          title: 'Dashboard Stats',
          gaTrack: true,
        },
        path: '/my/dashboard/stats',
        component: DashboardStatsPage,
      },
      {
        name: 'DashboardWorkouts',
        meta: {
          title: 'Dashboard Completed Workouts',
          gaTrack: true,
        },
        path: '/my/dashboard/:workoutType(workouts|custom-workouts)/:workoutCategory(top)?',
        component: DashboardWorkoutsPage,
      },
      {
        name: 'DashboardCommunity',
        meta: {
          title: 'Dashboard Community',
          gaTrack: true,
        },
        path: '/my/dashboard/:postType(comments|discussions)',
        component: DashboardCommunityPage,
      },

      /*
        analytics + tracker SPA routes
        - workout: title should mirror what is in VideoController->show
        - customWorkout: title should mirror what is in CustomWorkouts->show
       */
      // video workouts
      {
        name: 'WorkoutDetailDashboard',
        meta: {
          title: 'Workout Dashboard',
          gaTrack: true,
          scrollTo: '#spa-top',
        },
        path: '/videos/:slug/dashboard',
        component: WorkoutDetailDashboardPage,
        beforeEnter: (to, from, next) => {
          // register vuex notes module
          LoadNotesState(store).then(() => {
            next()
          })
        },
      },
      {
        name: 'WorkoutDetailCompletes',
        meta: {
          title: 'Workout Completes',
          gaTrack: true,
          scrollTo: '#spa-top',
        },
        path: '/videos/:slug/dashboard/completes',
        component: WorkoutDetailCompletesPage,
      },
      {
        name: 'WorkoutDetailNotes',
        meta: {
          title: 'Workout Notes',
          gaTrack: true,
          scrollTo: '#spa-top',
        },
        path: '/videos/:slug/dashboard/notes',
        component: WorkoutDetailNotesPage,
        beforeEnter: (to, from, next) => {
          // register vuex notes module
          LoadNotesState(store).then(() => {
            next()
          })
        },
      },
      // custom workouts
      {
        name: 'CustomWorkoutDetailDashboard',
        meta: {
          title: 'Workout Dashboard',
          gaTrack: true,
          scrollTo: '#spa-top',
        },
        path: '/my/custom-workouts/:id/:slug/dashboard',
        component: WorkoutDetailDashboardPage,
        beforeEnter: (to, from, next) => {
          // register vuex notes module
          LoadNotesState(store).then(() => {
            next()
          })
        },
      },
      {
        name: 'CustomWorkoutDetailCompletes',
        meta: {
          title: 'Workout Completes',
          gaTrack: true,
          scrollTo: '#spa-top',
        },
        path: '/my/custom-workouts/:id/:slug/dashboard/completes',
        component: WorkoutDetailCompletesPage,
      },
      {
        name: 'CustomWorkoutDetailNotes',
        meta: {
          title: 'Workout Notes',
          gaTrack: true,
          scrollTo: '#spa-top',
        },
        path: '/my/custom-workouts/:id/:slug/dashboard/notes',
        component: WorkoutDetailNotesPage,
        beforeEnter: (to, from, next) => {
          // register vuex notes module
          LoadNotesState(store).then(() => {
            next()
          })
        },
      },

      // organization dashboards
      {
        name: 'OrganizationDashboard',
        meta: {
          title: 'Organization Dashboard',
          gaTrack: true,
        },
        path: '/organizations/:slug/dashboard',
        component: OrganizationDashboardPage,
        beforeEnter: (to, from, next) => {
          // register vuex search module
          LoadOrganizationState(store).then(() => {
            next()
          })
        },
      },
      {
        name: 'OrganizationUsers',
        meta: {
          title: 'Organization Members',
          gaTrack: true,
        },
        path: '/organizations/:slug/users',
        component: OrganizationUsersPage,
        beforeEnter: (to, from, next) => {
          // register vuex search module
          LoadOrganizationState(store).then(() => {
            next()
          })
        },
      },
    ],
  })

  /** Attempted to implement Vue Router's scrollBehavior function to address
   * a few use cases where scroll positioning was present, couldn't be used.
   * Result was the function scrolled to the top of page even when returning
   * falsy/empty value.
   *
   * -VueRouter 3.0.6
   */

  // auto scroll, focus, and track pageviews in GA
  router.afterEach((to, from) => {
    // if not coming from an SPA route OR initial SPA page load (no name, not in routes)
    // do not do anything... do not want to double track in GA as
    // it was already tracked in initial blade load
    if (!from.name) {
      return
    }

    if (to.meta) {
      spaMetaUpdateAndTrack(to.meta)
    }
  })

  return router
}

export default createSpaRoutes
